import {
  concat,
  decrypt,
  encrypt,
  exportAesKey,
  importAesKey,
  randomBytes,
  utf8,
} from './primitives.ts';

/**
 * Asymmetrisches Schlüsselpaar pro Kunde und pro Buddy (Konzept 3.2 / 3.3).
 *
 * Zweck: Jeder kann mit dem öffentlichen Schlüssel eines Kunden ein Dokument
 * für ihn verschlüsseln, ohne dass der Kunde beteiligt ist – das ist die
 * Voraussetzung für die Blindablage (3.7). Mit einem rein passwortbasierten
 * symmetrischen Verfahren wäre das nicht sauber lösbar.
 */

export type KeyAlgorithm = 'X25519' | 'P-256';

/**
 * Das Konzept nennt X25519. Web Crypto unterstützt es in aktuellen Browsern,
 * aber nicht in allen, die bei der Zielgruppe realistisch im Einsatz sind.
 * Deshalb: X25519 wenn verfügbar, sonst ECDH P-256 – beides ist solide.
 * Welches Verfahren ein Konto nutzt, merkt sich der Server (Spalte
 * key_algorithm) und liefert es beim Entsperren mit.
 */
export async function selectAlgorithm(): Promise<KeyAlgorithm> {
  try {
    await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits']);
    return 'X25519';
  } catch {
    return 'P-256';
  }
}

function algorithmParams(algorithm: KeyAlgorithm): EcKeyGenParams | Algorithm {
  return algorithm === 'X25519'
    ? { name: 'X25519' }
    : { name: 'ECDH', namedCurve: 'P-256' };
}

export interface KeyPairMaterial {
  algorithm: KeyAlgorithm;
  publicKeyRaw: Uint8Array;
  privateKeyPkcs8: Uint8Array;
}

export async function generateKeyPair(
  algorithm: KeyAlgorithm,
): Promise<KeyPairMaterial> {
  const pair = (await crypto.subtle.generateKey(
    algorithmParams(algorithm) as EcKeyGenParams,
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  return {
    algorithm,
    publicKeyRaw: new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey)),
    privateKeyPkcs8: new Uint8Array(
      await crypto.subtle.exportKey('pkcs8', pair.privateKey),
    ),
  };
}

export async function importPublicKey(
  raw: Uint8Array,
  algorithm: KeyAlgorithm,
): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    raw as BufferSource,
    algorithmParams(algorithm) as EcKeyImportParams,
    true,
    [],
  );
}

export async function importPrivateKey(
  pkcs8: Uint8Array,
  algorithm: KeyAlgorithm,
): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'pkcs8',
    pkcs8 as BufferSource,
    algorithmParams(algorithm) as EcKeyImportParams,
    false,   // muss nie wieder exportiert werden
    ['deriveBits'],
  );
}

// ---------------------------------------------------------------------------
// Private Key schützen
// ---------------------------------------------------------------------------

/**
 * Verschlüsselt den privaten Schlüssel mit dem aus dem Zugriffspasswort
 * abgeleiteten Schlüssel. Nur in dieser Form erreicht er den Server.
 */
export async function wrapPrivateKey(
  privateKeyPkcs8: Uint8Array,
  passwordKey: CryptoKey,
): Promise<Uint8Array> {
  return encrypt(passwordKey, privateKeyPkcs8);
}

export async function unwrapPrivateKey(
  encrypted: Uint8Array,
  passwordKey: CryptoKey,
): Promise<Uint8Array> {
  try {
    return await decrypt(passwordKey, encrypted);
  } catch {
    // AES-GCM scheitert bei falschem Schlüssel an der Authentifizierung.
    // Für den Nutzer bedeutet das schlicht: falsches Passwort.
    throw new Error('Zugriffspasswort ist falsch');
  }
}

// ---------------------------------------------------------------------------
// DEK für einen Empfänger wrappen (Envelope Encryption, Konzept 3.1)
// ---------------------------------------------------------------------------

const HKDF_INFO = utf8.encode('it-karsten-fischer/dokumentensystem/dek-wrap/v2');

/**
 * ECDH liefert ein gemeinsames Geheimnis, keinen fertigen Schlüssel. HKDF
 * macht daraus einen gleichverteilten AES-Schlüssel. `info` bindet ihn an
 * genau diesen Verwendungszweck: derselbe Schlüsselaustausch für einen
 * anderen Zweck ergäbe einen anderen Schlüssel.
 */
async function deriveSharedKey(
  privateKey: CryptoKey,
  publicKey: CryptoKey,
  algorithm: KeyAlgorithm,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits(
    algorithm === 'X25519'
      ? ({ name: 'X25519', public: publicKey } as unknown as AlgorithmIdentifier)
      : ({ name: 'ECDH', public: publicKey } as EcdhKeyDeriveParams),
    privateKey,
    algorithm === 'X25519' ? 256 : 256,
  );

  const hkdfKey = await crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveBits']);
  const derived = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: HKDF_INFO as BufferSource },
    hkdfKey,
    256,
  );
  return importAesKey(new Uint8Array(derived));
}

const EPHEMERAL_PUBLIC_LENGTH: Record<KeyAlgorithm, number> = {
  // Rohformat: X25519 = 32 Byte, P-256 unkomprimiert = 65 Byte (0x04 + x + y)
  X25519: 32,
  'P-256': 65,
};

const WRAP_SALT_BYTES = 16;

/**
 * Wrappt beliebige Bytes für einen Empfänger, dessen öffentlichen Schlüssel
 * man kennt. Es wird ein Einmal-Schlüsselpaar erzeugt (ephemeral-static
 * ECDH), dessen öffentlicher Teil dem Ergebnis vorangestellt wird.
 *
 * Format: [ephemeraler Public Key | HKDF-Salt (16) | Nonce (12) | Ciphertext]
 *
 * Gemeinsame Grundlage für `wrapDekFor` (DEK als CryptoKey) und die
 * Schlüsselkreise (Organisations-/Kreis-Schlüssel als rohe PKCS8-Bytes,
 * `wrapBytesFor`) — beide brauchen exakt dasselbe Umschlagformat, nur mit
 * unterschiedlicher Nutzlast.
 */
export async function wrapBytesFor(
  bytes: Uint8Array,
  recipientPublicKeyRaw: Uint8Array,
  algorithm: KeyAlgorithm,
): Promise<Uint8Array> {
  const recipientPublicKey = await importPublicKey(recipientPublicKeyRaw, algorithm);

  const ephemeral = (await crypto.subtle.generateKey(
    algorithmParams(algorithm) as EcKeyGenParams,
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  const salt = randomBytes(WRAP_SALT_BYTES);
  const sharedKey = await deriveSharedKey(
    ephemeral.privateKey,
    recipientPublicKey,
    algorithm,
    salt,
  );

  const ephemeralPublicRaw = new Uint8Array(
    await crypto.subtle.exportKey('raw', ephemeral.publicKey),
  );
  const wrapped = await encrypt(sharedKey, bytes);

  return concat(ephemeralPublicRaw, salt, wrapped);
}

/** Gegenstück zu wrapBytesFor – mit dem eigenen privaten Schlüssel. */
export async function unwrapBytes(
  wrapped: Uint8Array,
  ownPrivateKey: CryptoKey,
  algorithm: KeyAlgorithm,
): Promise<Uint8Array> {
  const publicLength = EPHEMERAL_PUBLIC_LENGTH[algorithm];
  if (wrapped.length <= publicLength + WRAP_SALT_BYTES) {
    throw new Error('Gewrappter Schlüssel hat ein unerwartetes Format');
  }

  const ephemeralPublicRaw = wrapped.subarray(0, publicLength);
  const salt = wrapped.subarray(publicLength, publicLength + WRAP_SALT_BYTES);
  const payload = wrapped.subarray(publicLength + WRAP_SALT_BYTES);

  const ephemeralPublicKey = await importPublicKey(ephemeralPublicRaw, algorithm);
  const sharedKey = await deriveSharedKey(
    ownPrivateKey,
    ephemeralPublicKey,
    algorithm,
    salt,
  );

  return decrypt(sharedKey, payload);
}

/** Wrappt einen DEK für einen Empfänger — dünne Hülle um `wrapBytesFor`. */
export async function wrapDekFor(
  dek: CryptoKey,
  recipientPublicKeyRaw: Uint8Array,
  algorithm: KeyAlgorithm,
): Promise<Uint8Array> {
  return wrapBytesFor(await exportAesKey(dek), recipientPublicKeyRaw, algorithm);
}

/** Gegenstück zu wrapDekFor – mit dem eigenen privaten Schlüssel. */
export async function unwrapDek(
  wrapped: Uint8Array,
  ownPrivateKey: CryptoKey,
  algorithm: KeyAlgorithm,
): Promise<CryptoKey> {
  return importAesKey(await unwrapBytes(wrapped, ownPrivateKey, algorithm));
}
